import useModalTrigger from 'magik-react-hooks/useModalTrigger'
import { Link } from 'react-router-dom'
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap'
import { useAuthUser } from 'use-eazy-auth'
import Header from '../../components/Header'
import MissingTranslations from '../../components/MissingTranslations'
import Page from '../../components/Page'
import Paginator from '../../components/Paginator'
import Sorter, { SortControl } from '../../components/Sorter'
import StickyTable from '../../components/StickyTable'
import { useContents, useDeleteContent } from '../../hooks/contents'
import { useQsFilters } from '../../hooks/filters'
import { Content, User } from '../../types'

const initFilters = (params: URLSearchParams) => ({
  page: Number(params.get('page') ?? 1),
  search: params.get('search') ?? '',
  ordering: params.get('ordering') ?? '',
})

const MISSING_TRANSLATIONS_FIELDS = ['text_plain']

export default function ContentsList() {
  const { filters, uiFilters, setFiltersDebounced, setFilters } =
    useQsFilters(initFilters)
  const { data } = useContents(filters)
  const { user } = useAuthUser<User>()
  const deleteContent = useDeleteContent()
  const [modalRm, modalRmActions] = useModalTrigger<Content>()

  return (
    <Page padded>
      <Header>
        <div className="d-flex align-items-end">
          <h2 className="m-0">Contenuti</h2>
          <div className="counter ms-2 mb-1">
            <small>
              {data!.count}
              {' / '}
              {data!.total_count}
            </small>
          </div>
        </div>
        {user!.is_superuser && (
          <Link className="btn btn-primary" to="/contents/new">
            <i className="bi bi-plus-circle me-2"></i> Aggiungi
          </Link>
        )}
      </Header>
      <div className="d-flex justify-content-between mt-3 mb-3 align-items-center">
        <input
          placeholder="Cerca"
          className="form-control w-auto"
          type="text"
          value={uiFilters.search}
          onChange={(e) =>
            setFiltersDebounced({ search: e.target.value, page: 1 })
          }
        />
      </div>
      <StickyTable className='flex-1'>
        <thead>
          <Sorter
            value={uiFilters.ordering}
            onSortChange={(ordering) =>
              setFilters({
                ordering,
                page: 1,
              })
            }
          >
            <tr>
              <th>#</th>
              <th>
                <div className="d-flex align-items-center">
                  Codice
                  <SortControl field="code" />
                </div>
              </th>
              <th>
                <div className="d-flex align-items-center">
                  Titolo
                  <SortControl field="label" />
                </div>
              </th>
              {user!.is_superuser && <th></th>}
            </tr>
          </Sorter>
        </thead>
        <tbody>
          {data!.results.map((content) => (
            <tr key={content.id}>
              <td>{content.id}</td>
              <td>
                <Link to={`/contents/${content.id}`}>{content.code}</Link>
                <MissingTranslations
                  data={content}
                  fields={MISSING_TRANSLATIONS_FIELDS}
                />
              </td>
              <td>{content.title['it']}</td>
              {user!.is_superuser && (
                <td>
                  <i
                    onClick={() => modalRmActions.open(content)}
                    className="bi cursor-pointer text-danger bi-trash3-fill"
                  ></i>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </StickyTable>
      <Modal centered isOpen={modalRm.isOpen} toggle={modalRmActions.toggle}>
        <ModalHeader toggle={modalRmActions.toggle}>Elimina</ModalHeader>
        <ModalBody>
          {modalRm.value && (
            <div className="pt-3 pb-3">
              Sei sicuro di voler eliminare il Contenuto {modalRm.value.code} ?
            </div>
          )}
        </ModalBody>
        <ModalFooter className="d-flex justify-content-between">
          <button
            onClick={modalRmActions.toggle}
            className="btn btn-outline-primary"
          >
            Annulla
          </button>
          <button
            className="btn  btn-danger"
            disabled={deleteContent.isLoading}
            onClick={async () => {
              await deleteContent.mutateAsync(modalRm.value!.id)
              modalRmActions.close()
            }}
          >
            <i className="bi bi-trash3-fill"></i> Elimina
          </button>
        </ModalFooter>
      </Modal>
      <div className="position-relative">
        <Paginator
          count={data!.count}
          currentPage={uiFilters.page}
          goToPage={(page) => setFilters({ page })}
        />
      </div>
    </Page>
  )
}
